/**
 * Prefill bridge into the refinance wizard (WI-TV-W5-002).
 *
 * Refinance can be entered from a `?loanPurpose=refinance` query, from a
 * TurboVets prefill message, or from the live loan-purpose control. In every
 * case the WI-TV-W5-002 answers the borrower already supplied — refinance goal,
 * manual property address, property state/type, estimated value, current
 * balance, and requested loan amount — must reach the wizard's initial state
 * instead of being discarded.
 *
 * Trust boundaries are unchanged: this module never listens for messages and
 * never decides what is trusted. Prefill values arrive here only after
 * `handlePrefillMessage` has accepted the sender's exact allowlisted origin and
 * applied them to the form, so values from a disallowed origin are never
 * present to read. Query values are untrusted text and are validated here the
 * same way the wizard validates typed answers: anything that is not an
 * authoritative enum value, a five-digit ZIP, or an exactly representable
 * positive amount is dropped rather than seeded. Values are only seeded — the
 * wizard still starts on step 1, every control stays editable, and nothing is
 * auto-advanced or submitted. No value is ever logged.
 */
import {
  CREDIT_SCORE_RANGE_OPTIONS,
  MILITARY_COMPONENT_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  REFINANCE_GOAL_OPTIONS,
} from "../models/referral";
import { mapMilitaryBranchToLmd } from "../models/lmd";
import { isValidStateCode } from "../form/validation";
import { canonicalMoneyText, parseMoneyInput } from "./money";
import { readCoBorrowerRawFromMapped, sanitizeCoBorrowerValues } from "./coborrower-values";
import {
  CREDIT_SCORE_RANGE_KEY,
  CURRENT_LOAN_BALANCE_KEY,
  EMAIL_KEY,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  MILITARY_ANSWER_KEY,
  MILITARY_ANSWER_OPTIONS,
  MILITARY_COMPONENT_KEY,
  PHONE_KEY,
  isValidEmailInput,
  isValidPhoneInput,
  phoneDigits,
  ESTIMATED_VALUE_KEY,
  LOAN_AMOUNT_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_STATE_KEY,
  PROPERTY_STREET_KEY,
  PROPERTY_TYPE_KEY,
  PROPERTY_ZIP_KEY,
  PROPERTY_ZIP_REGEX,
  REFINANCE_TYPE_KEY,
} from "./refinance-steps";

/**
 * The value keys the wizard can be seeded with, covering every Wave 5 field
 * implemented through WI-TV-W5-003. These are the authoritative referral field
 * names, so query parameters, prefill payload keys, and form control names all
 * already agree. Military status and later-work-item fields are deliberately
 * absent and are never seeded or collected.
 *
 * The optional co-borrower answers (WI-TV-W7-001) are deliberately absent too.
 * They are namespaced wizard state rather than referral field names, they have
 * no query-string contract, and they are seeded only through the nested
 * `coBorrower` object of an exact-origin prefill message, so listing them here
 * would open a channel the co-borrower contract does not have.
 */
export const REFINANCE_PREFILL_KEYS = [
  REFINANCE_TYPE_KEY,
  PROPERTY_STREET_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_STATE_KEY,
  PROPERTY_ZIP_KEY,
  PROPERTY_TYPE_KEY,
  ESTIMATED_VALUE_KEY,
  CURRENT_LOAN_BALANCE_KEY,
  LOAN_AMOUNT_KEY,
  CREDIT_SCORE_RANGE_KEY,
  MILITARY_ANSWER_KEY,
  MILITARY_COMPONENT_KEY,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  PHONE_KEY,
  EMAIL_KEY,
] as const;

/**
 * Wizard value key -> legacy form control `name` for the keys whose control is
 * nested under the borrower. Every other key already matches its control name.
 */
const FORM_CONTROL_NAMES: Readonly<Record<string, string>> = {
  [CREDIT_SCORE_RANGE_KEY]: "borrower.creditScoreRange",
  [FIRST_NAME_KEY]: "borrower.firstName",
  [LAST_NAME_KEY]: "borrower.lastName",
  [PHONE_KEY]: "borrower.phone",
  [EMAIL_KEY]: "borrower.email",
};

const MONEY_KEYS = [ESTIMATED_VALUE_KEY, CURRENT_LOAN_BALANCE_KEY, LOAN_AMOUNT_KEY] as const;

/**
 * Keeps only the values the wizard would itself accept. Unknown
 * keys, blank values, non-enum goals/property types, invalid state codes,
 * malformed ZIP codes, and amounts that are not exactly representable positive
 * money are dropped, so a seeded wizard can never start from a value the user
 * could not have typed. Money is normalized to canonical numeric text.
 *
 * Optional co-borrower answers ride along under their own namespaced keys and
 * are sanitized by the shared co-borrower model, which also carries the opt-in.
 * They are journey-neutral, so both wizards accept exactly the same secondary
 * answers, and they never touch a primary-borrower key.
 */
export function sanitizeRefinancePrefill(
  raw: Readonly<Record<string, string | undefined>>,
): Record<string, string> {
  const seeded: Record<string, string> = {};
  const text = (key: string): string => (raw[key] ?? "").trim();

  const goal = text(REFINANCE_TYPE_KEY);
  if (REFINANCE_GOAL_OPTIONS.some((o) => o.value === goal)) {
    seeded[REFINANCE_TYPE_KEY] = goal;
  }

  for (const key of [PROPERTY_STREET_KEY, PROPERTY_CITY_KEY]) {
    const value = text(key);
    if (value !== "") seeded[key] = value;
  }

  // State codes are compared case-insensitively but seeded in the exact casing
  // the authoritative state options use, so the control renders as selected.
  const state = text(PROPERTY_STATE_KEY).toUpperCase();
  if (isValidStateCode(state)) seeded[PROPERTY_STATE_KEY] = state;

  const zip = text(PROPERTY_ZIP_KEY);
  if (PROPERTY_ZIP_REGEX.test(zip)) seeded[PROPERTY_ZIP_KEY] = zip;

  const propertyType = text(PROPERTY_TYPE_KEY);
  if (PROPERTY_TYPE_OPTIONS.some((o) => o.value === propertyType)) {
    seeded[PROPERTY_TYPE_KEY] = propertyType;
  }

  for (const key of MONEY_KEYS) {
    const parsed = parseMoneyInput(text(key));
    if (parsed !== null && parsed > 0) seeded[key] = canonicalMoneyText(parsed);
  }

  const credit = text(CREDIT_SCORE_RANGE_KEY);
  if (CREDIT_SCORE_RANGE_OPTIONS.some((o) => o.value === credit)) {
    seeded[CREDIT_SCORE_RANGE_KEY] = credit;
  }

  // The military answer and its component are seeded independently, and the
  // component is only kept for an affirmative answer, so a seeded application
  // can never carry a component the borrower could not currently see.
  const militaryAnswer = text(MILITARY_ANSWER_KEY);
  if (MILITARY_ANSWER_OPTIONS.some((o) => o.value === militaryAnswer)) {
    seeded[MILITARY_ANSWER_KEY] = militaryAnswer;
  }
  const component = text(MILITARY_COMPONENT_KEY);
  if (
    seeded[MILITARY_ANSWER_KEY] === "yes" &&
    MILITARY_COMPONENT_OPTIONS.some((o) => o.value === component)
  ) {
    seeded[MILITARY_COMPONENT_KEY] = component;
  }

  for (const key of [FIRST_NAME_KEY, LAST_NAME_KEY]) {
    const value = text(key);
    if (value !== "") seeded[key] = value;
  }

  const phone = text(PHONE_KEY);
  if (isValidPhoneInput(phone)) seeded[PHONE_KEY] = phoneDigits(phone);

  const email = text(EMAIL_KEY);
  if (isValidEmailInput(email)) seeded[EMAIL_KEY] = email;

  Object.assign(seeded, sanitizeCoBorrowerValues(raw));

  return seeded;
}

/**
 * Translates the existing nested `militaryService` prefill contract into the
 * wizard's application-level answer and component.
 *
 * The published prefill contract predates the wizard and carries
 * `militaryService.isMilitary` / `militaryService.branch`, which the legacy form
 * accepts but the wizard has no control for. Integrators still sending it must
 * keep working, so an accepted payload's mapped values are translated here
 * rather than dropped. Only the exact branches with an unambiguous LMD
 * equivalent translate; anything else (including a National Guard or Reserves
 * style value, which LMD splits into two enum members) yields no component
 * rather than a guess. Service status is deliberately not translated.
 *
 * `mapped` contains only values already accepted from an exact allowlisted
 * origin; the result is still sanitized like every other prefill value.
 */
export function translateLegacyMilitaryPrefill(
  mapped: Readonly<Record<string, string>>,
): Record<string, string> {
  const translated: Record<string, string> = {};

  const legacyIsMilitary = (mapped["isMilitary"] ?? "").trim().toLowerCase();
  if (legacyIsMilitary === "true") translated[MILITARY_ANSWER_KEY] = "yes";
  else if (legacyIsMilitary === "false") translated[MILITARY_ANSWER_KEY] = "no";

  const legacyBranch = (mapped["militaryBranch"] ?? "").trim();
  if (translated[MILITARY_ANSWER_KEY] === "yes" && mapMilitaryBranchToLmd(legacyBranch)) {
    translated[MILITARY_COMPONENT_KEY] = legacyBranch;
  }

  // An answer sent under the wizard's own keys is the more specific statement
  // and wins over the legacy nested one.
  if (mapped[MILITARY_ANSWER_KEY] !== undefined) {
    translated[MILITARY_ANSWER_KEY] = mapped[MILITARY_ANSWER_KEY];
    delete translated[MILITARY_COMPONENT_KEY];
  }
  if (mapped[MILITARY_COMPONENT_KEY] !== undefined) {
    translated[MILITARY_COMPONENT_KEY] = mapped[MILITARY_COMPONENT_KEY];
  }

  return translated;
}

/**
 * Reads the wizard values carried by a URL search string (the
 * `?loanPurpose=refinance` entry path) and sanitizes them for the wizard.
 */
export function readRefinancePrefillFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const raw: Record<string, string> = {};
  for (const key of REFINANCE_PREFILL_KEYS) {
    const value = params.get(key);
    if (value !== null) raw[key] = value;
  }
  return sanitizeRefinancePrefill(raw);
}

/**
 * Sanitizes the mapped values of an accepted prefill message for the wizard,
 * without going through a legacy form.
 *
 * This is the direct-refinance entry path: the wizard is the only mounted
 * experience, so there are no legacy controls to apply the payload to and read
 * back from. `mapped` is the output of `mapPrefillPayload` for a message whose
 * exact allowlisted origin was already accepted, so it is keyed by legacy
 * control `name`; the borrower-scoped names are translated back to their wizard
 * value keys and the legacy nested military answer is translated the same way it
 * is on the form path. Everything is then sanitized exactly like a query seed.
 */
export function readRefinancePrefillFromMapped(
  mapped: Readonly<Record<string, string>>,
): Record<string, string> {
  const raw: Record<string, string> = {
    ...translateLegacyMilitaryPrefill(mapped),
    // The nested `coBorrower` object is the only co-borrower prefill channel;
    // an absent or empty one contributes nothing and removes nothing.
    ...readCoBorrowerRawFromMapped(mapped),
  };
  for (const key of REFINANCE_PREFILL_KEYS) {
    const name = FORM_CONTROL_NAMES[key] ?? key;
    const value = mapped[name];
    if (value !== undefined) raw[key] = value;
  }
  return sanitizeRefinancePrefill(raw);
}

/**
 * Reads the WI-TV-W5-002 values currently held by the legacy form controls and
 * sanitizes them for the wizard. This is the postMessage and live-selection
 * path: an accepted prefill from an allowlisted origin has already been applied
 * to these controls, so reading them carries exactly the values that were
 * trusted, and nothing from a rejected message is present to read.
 */
export function readRefinancePrefillFromForm(
  root: ParentNode,
  extra: Readonly<Record<string, string>> = {},
): Record<string, string> {
  // `extra` carries accepted prefill values that the legacy form has no control
  // for (the application military answer), so they survive the handoff into the
  // wizard. They are sanitized here exactly like every other prefill value.
  const raw: Record<string, string> = { ...extra };
  for (const key of REFINANCE_PREFILL_KEYS) {
    const name = FORM_CONTROL_NAMES[key] ?? key;
    const control = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
    if (control) raw[key] = control.value;
  }
  return sanitizeRefinancePrefill(raw);
}
